import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export const clientNeedsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "clientNeedsApi",
    baseUrl: "/api/client-needs",
  }),
  reducerPath: "clientNeedsApi",
  tagTypes: ["ClientNeeds", "ClientNeedPreferences"],
  endpoints: (builder) => ({
    getClientNeeds: builder.query<
      any,
      {
        include?: string;
        search?: string;
        nextId?: string;
        limit?: number;
        postalCode?: string;
        radius?: number;
        propertyType?: string;
        budgetMin?: number;
        budgetMax?: number;
        features?: string;
        personId?: string;
        status?: string;
        lookingFor?: string;
        excludeCreator?: boolean;
        /** REP-206 §2 — only standalone (orphaned) needs, i.e. not yet tied
         *  to a rep agreement. Used by the "Attach an existing need" picker. */
        unrepresentedOnly?: boolean;
        /** REP-207 §2 — "Mine vs Global". 'mine' = needs I created or
         *  represent (a rep agreement I own). Absent = all needs (global). */
        scope?: "mine" | "global";
      }
    >({
      query: (args) => {
        const queryParams = new URLSearchParams();

        if (args.search) queryParams.append("query", args.search);
        if (args.include) queryParams.append("include", args.include);
        if (args.limit) queryParams.append("limit", args.limit.toString());
        if (args.nextId) queryParams.append("nextId", args.nextId);

        // Filter parameters
        const filterParams = [
          "postalCode",
          "propertyType",
          "features",
          "personId",
          "status",
          "lookingFor",
        ];
        filterParams.forEach((param) => {
          if (args[param as keyof typeof args]) {
            queryParams.append(param, args[param as keyof typeof args] as string);
          }
        });

        // Numeric parameters
        if (args.budgetMin !== undefined)
          queryParams.append("budgetMin", args.budgetMin.toString());
        if (args.budgetMax !== undefined)
          queryParams.append("budgetMax", args.budgetMax.toString());
        if (args.radius !== undefined)
          queryParams.append("radius", args.radius.toString());

        // Boolean parameters
        if (args.excludeCreator)
          queryParams.append("excludeCreator", "true");
        if (args.unrepresentedOnly)
          queryParams.append("unrepresentedOnly", "true");

        // REP-207 §2 — Mine/Global scope (only sent when 'mine'; 'global'
        // is the server default of no filter).
        if (args.scope === "mine") queryParams.append("scope", "mine");

        return `?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.data?.data
          ? [
              ...result.data.data.map(
                ({ id }: any) => ({ type: "ClientNeeds", id } as const)
              ),
              "ClientNeeds",
            ]
          : ["ClientNeeds"],
    }),

    getClientNeedById: builder.query<any, { id?: string; include?: string }>({
      query: (args) => {
        const queryParams = args.include ? `?include=${args.include}` : "";
        return `/${args.id}${queryParams}`;
      },
      providesTags: (_result, _error, args) => [
        { type: "ClientNeeds", id: args.id },
      ],
    }),

    /**
     * GET /api/client-needs/by-buyer-rep-project/:projectId
     *
     * Resolve the (singleton) draft ClientNeed attached to a demand-side
     * rep project (BUYER_REP_AGREEMENT or TENANT_REP_AGREEMENT). Returns
     * null when no need exists yet — the onboarding UI uses that to
     * render a "no need yet" state.
     */
    getClientNeedByBuyerRepProject: builder.query<
      any,
      { projectId: string; include?: string }
    >({
      query: ({ projectId, include }) => {
        const queryParams = include ? `?include=${include}` : "";
        return `/by-buyer-rep-project/${projectId}${queryParams}`;
      },
      providesTags: (_result, _error, { projectId }) => [
        { type: "ClientNeeds", id: `buyer-rep:${projectId}` },
      ],
    }),

    createClientNeed: builder.mutation<any, any>({
      query: (body) => ({
        url: "/",
        method: "POST",
        body: body,
      }),
      invalidatesTags: [{ type: "ClientNeeds" }],
    }),

    updateClientNeed: builder.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ClientNeeds", id },
        { type: "ClientNeeds" },
      ],
    }),

    deleteClientNeed: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ClientNeeds" }],
    }),

    // Property linking
    linkProperty: builder.mutation<any, { clientNeedId: string; propertyId: string }>({
      query: ({ clientNeedId, propertyId }) => ({
        url: `/${clientNeedId}/properties`,
        method: "POST",
        body: { propertyId },
      }),
      invalidatesTags: (_result, _error, { clientNeedId }) => [
        { type: "ClientNeeds", id: clientNeedId },
      ],
    }),

    unlinkProperty: builder.mutation<void, { clientNeedId: string; propertyId: string }>({
      query: ({ clientNeedId, propertyId }) => ({
        url: `/${clientNeedId}/properties/${propertyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { clientNeedId }) => [
        { type: "ClientNeeds", id: clientNeedId },
      ],
    }),

    updateLinkedProperty: builder.mutation<
      any,
      {
        clientNeedId: string;
        propertyId: string;
        updates: {
          /** Unified bucket — the single source of truth. SHORTLIST | PASSED | IN_PURSUIT. */
          status?: "SHORTLIST" | "PASSED" | "IN_PURSUIT";
        };
      }
    >({
      query: ({ clientNeedId, propertyId, updates }) => ({
        url: `/${clientNeedId}/properties/${propertyId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { clientNeedId }) => [
        { type: "ClientNeeds", id: clientNeedId },
        { type: "ClientNeeds" },
      ],
    }),

    /**
     * POST /api/client-needs/:id/send-for-review
     *
     * REP-207 §6 — email the need's client a link to review (approve/deny)
     * the shortlisted properties.
     */
    sendNeedForReview: builder.mutation<
      { data: { sent: boolean; email: string | null } },
      { clientNeedId: string }
    >({
      query: ({ clientNeedId }) => ({
        url: `/${clientNeedId}/send-for-review`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { clientNeedId }) => [
        { type: "ClientNeeds", id: clientNeedId },
      ],
    }),

    // Convert to deal
    convertToDeal: builder.mutation<any, string>({
      query: (clientNeedId) => ({
        url: `/${clientNeedId}/convert`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, clientNeedId) => [
        { type: "ClientNeeds", id: clientNeedId },
        { type: "ClientNeeds" },
      ],
    }),

    /**
     * POST /api/client-needs/:id/spin-off
     *
     * Spin the need's attached properties into a deal (1..N). The server
     * resolves whether the resulting pursuit is tied to the need's rep
     * agreement or standalone, and returns the parent deal it created/joined.
     */
    spinOffNeed: builder.mutation<
      { data: { parentDealId: string } },
      {
        clientNeedId: string;
        propertyIds: string[];
        /** New pursuit's name — used on creation only (ignored if the parent
         *  already exists; server falls back to the derived default if empty). */
        parentName?: string;
      }
    >({
      query: ({ clientNeedId, propertyIds, parentName }) => ({
        url: `/${clientNeedId}/spin-off`,
        method: "POST",
        body: parentName?.trim()
          ? { propertyIds, parentName }
          : { propertyIds },
      }),
      invalidatesTags: (_result, _error, { clientNeedId }) => [
        { type: "ClientNeeds", id: clientNeedId },
        { type: "ClientNeeds" },
      ],
    }),

    /**
     * POST /api/client-needs/:id/start-rep-agreement
     *
     * REP-206 §2 — "Start a rep agreement from this need." Creates a
     * demand-side rep-agreement project and ADOPTS this existing
     * (unrepresented) need onto it — no duplicate DRAFT need is created.
     * Returns the new project id (the rep agreement deal) + the need id.
     */
    startRepAgreementFromNeed: builder.mutation<
      { data: { projectId: string; clientNeedId: string } },
      {
        clientNeedId: string;
        internalName?: string;
        // Demand rep type chosen by the user (a client need is demand-side,
        // so the valid types are Buyer or Tenant rep). Omitted → server infers
        // from the need's economics (SALE → buyer, LEASE → tenant).
        repType?: "BUYER_REP_AGREEMENT" | "TENANT_REP_AGREEMENT";
      }
    >({
      query: ({ clientNeedId, internalName, repType }) => ({
        url: `/${clientNeedId}/start-rep-agreement`,
        method: "POST",
        body: {
          ...(internalName?.trim() ? { internalName } : {}),
          ...(repType ? { repType } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { clientNeedId }) => [
        { type: "ClientNeeds", id: clientNeedId },
        { type: "ClientNeeds" },
      ],
    }),

    /**
     * POST /api/client-needs/:id/attach-to-rep-project
     *
     * REP-206 §2 — adopt a standalone (orphaned) need onto an EXISTING
     * demand-side rep-agreement project. Swaps the project's auto-spawned
     * DRAFT need for this one and re-wires StepClientNeed server-side, so no
     * duplicate need is left behind. Invalidates the by-buyer-rep-project
     * resolver so the rep page's ClientNeedStepCard re-reads the new need.
     */
    attachNeedToRepProject: builder.mutation<
      { data: { projectId: string; clientNeedId: string } },
      { clientNeedId: string; projectId: string }
    >({
      query: ({ clientNeedId, projectId }) => ({
        url: `/${clientNeedId}/attach-to-rep-project`,
        method: "POST",
        body: { projectId },
      }),
      invalidatesTags: (_result, _error, { clientNeedId, projectId }) => [
        { type: "ClientNeeds", id: clientNeedId },
        { type: "ClientNeeds", id: `buyer-rep:${projectId}` },
        { type: "ClientNeeds" },
      ],
    }),

    // User preferences
    getPreferences: builder.query<any, void>({
      query: () => "/preferences",
      providesTags: ["ClientNeedPreferences"],
    }),

    updatePreferences: builder.mutation<any, any>({
      query: (body) => ({
        url: "/preferences",
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["ClientNeedPreferences"],
    }),
  }),
});

export const {
  useGetClientNeedsQuery,
  useLazyGetClientNeedsQuery,
  useGetClientNeedByIdQuery,
  useGetClientNeedByBuyerRepProjectQuery,
  useCreateClientNeedMutation,
  useUpdateClientNeedMutation,
  useDeleteClientNeedMutation,
  useLinkPropertyMutation,
  useUnlinkPropertyMutation,
  useUpdateLinkedPropertyMutation,
  useSendNeedForReviewMutation,
  useConvertToDealMutation,
  useSpinOffNeedMutation,
  useStartRepAgreementFromNeedMutation,
  useAttachNeedToRepProjectMutation,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
} = clientNeedsApi;
